import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Divider,
  Alert,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getFirmData } from "../../../redux/Apps/Lawyerapp/DashboardSlice/DashboardSlice";
import { backend_api } from "../../../static/server";
import { useDialog } from "../../DialogContext";

const BillGenerateForm = ({ entryData }) => {
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((data) => data.user);
  const { firmData } = useSelector((state) => state.dashboard);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    dispatch(
      getFirmData({
        url: `${backend_api}firm/create-law-firm/`,
        token: token,
      })
    );
  }, [dispatch, token]);

  const onlyFirstFirm =
    Array.isArray(firmData) && firmData.length > 0 ? firmData[0] : null;

  // Group data by matter (case ID)
  const matterGroups = useMemo(() => {
    const groups = {};

    // Process data based on format
    if (!entryData) return {};

    // Handle single row from handleQuickBill
    if (entryData.row) {
      const row = entryData.row;
      const caseId = row.case;
      const showOnBill = !row.non_billable || row.bill_entry_show;
      if (showOnBill) {
        groups[caseId] = {
          matterName: row.matter_name,
          data: [row],
          ids: [row.id],
          client: row.client, // Store client ID
          billGenerated: row.bill_genrated || false,
        };
      }
    }
    // Handle array of rows from handleBulkBill
    else if (Array.isArray(entryData)) {
      entryData.forEach((item) => {
        const row = item.row || item;
        const caseId = row.case;

        const showOnBill = !row.non_billable || row.bill_entry_show;
        if (!showOnBill) {
          return;
        }

        if (!groups[caseId]) {
          groups[caseId] = {
            matterName: row.matter_name,
            data: [],
            ids: [],
            client: row.client, // Store client ID
            billGenerated: false,
          };
        }

        // Only add non-generated bill entries to data and ids
        if (!row.bill_genrated) {
          groups[caseId].data.push(row);
          groups[caseId].ids.push(row.id);
        } else {
          // Mark the group as having at least one generated bill
          groups[caseId].billGenerated = true;

          // Still store the row for display purposes
          groups[caseId].data.push(row);
        }
      });

      // If all entries for a matter have bill_genrated=true, mark the entire matter as generated
      Object.keys(groups).forEach((caseId) => {
        const nonGeneratedEntries = groups[caseId].data.filter(
          (row) => !row.bill_genrated
        );
        if (
          nonGeneratedEntries.length === 0 &&
          groups[caseId].data.length > 0
        ) {
          groups[caseId].allGenerated = true;
        }

        // Update ids to only include non-generated entries
        groups[caseId].ids = nonGeneratedEntries.map((row) => row.id);
      });
    }

    return groups;
  }, [entryData]);

  // Get an array of matter IDs for tabs
  const matterIds = useMemo(() => Object.keys(matterGroups), [matterGroups]);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      issue_date: dayjs().format("YYYY-MM-DD"),
      due_date: dayjs().format("YYYY-MM-DD"),
      status: "draft",
    },
  });

  const onSubmit = async (data) => {
    // console.log("bill genrage data", data);
    const currentMatterId = matterIds[activeTab];
    const currentMatter = matterGroups[currentMatterId];

    if (!currentMatter) {
      toast.error("No matter data found");
      return;
    }

    // Skip if all bills for this matter are already generated
    if (currentMatter.allGenerated || currentMatter.ids.length === 0) {
      toast.info(
        `Bills for ${currentMatter.matterName} have already been generated`
      );

      // Move to the next tab if available
      if (activeTab < matterIds.length - 1) {
        setActiveTab(activeTab + 1);
      } else {
        navigate("/dashboard/bills");
        closeDialog();
      }
      return;
    }

    // Ensure we have a client ID
    if (!currentMatter.client) {
      toast.error("Client information is missing");
      return;
    }

    const body = {
      ...data,
      firm: onlyFirstFirm?.id,
      case: parseInt(currentMatterId),
      client: parseInt(currentMatter.client), // Ensure client ID is included and parsed as integer
      expenses: currentMatter.ids,
    };


    try {
      await axios.post(`${backend_api}firm-case/bill/`, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      toast.success(
        `Bill drafted successfully for ${currentMatter.matterName}`
      );

      // If this is the last matter, navigate away
      if (activeTab === matterIds.length - 1) {
        navigate("/dashboard/bills");
        closeDialog();
      } else {
        // Move to the next tab
        setActiveTab(activeTab + 1);
      }
    } catch (error) {
      toast.error("Failed to create bill");
      console.error("Error creating bill:", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const columns = [
    { field: "matter_name", headerName: "Matter", width: 150 },
    { field: "due_date", headerName: "Due Date", width: 150 },
    { field: "type", headerName: "Type", width: 100 },
    { field: "description", headerName: "Description", width: 200 },
    { field: "category_name", headerName: "Category", width: 150 },
    { field: "quantity", headerName: "Qty", width: 100 },
    { field: "rate", headerName: "Rate($)", width: 150 },
    { field: "billable_amount", headerName: "Billable($)", width: 150 },
    { field: "bill_status", headerName: "Status", width: 150 },
  ];

  // Get current matter data for the table
  const currentMatterData = useMemo(() => {
    const currentMatterId = matterIds[activeTab];
    if (!currentMatterId || !matterGroups[currentMatterId]) return [];

    return matterGroups[currentMatterId].data.map((item) => ({
      ...item,
      due_date: watch("due_date") || dayjs().format("YYYY-MM-DD"),
      bill_status: item.bill_genrated ? "Bill Generated" : "Ready to Draft",
    }));
  }, [matterIds, activeTab, matterGroups, watch("due_date")]);

  // Calculate total billable amount for current matter (only for non-generated bills)
  const totalBillableAmount = useMemo(() => {
    if (!currentMatterData.length) return 0;

    return currentMatterData
      .filter((item) => !item.bill_genrated) // Only count non-generated bills
      .reduce((sum, item) => {
        const amount = parseFloat(item.billable_amount) || 0;
        return sum + amount;
      }, 0)
      .toFixed(2);
  }, [currentMatterData]);

  // Check if current matter has any billable entries
  const currentMatterHasBillableEntries = useMemo(() => {
    const currentMatterId = matterIds[activeTab];
    if (!currentMatterId || !matterGroups[currentMatterId]) return false;

    return matterGroups[currentMatterId].ids.length > 0;
  }, [matterIds, activeTab, matterGroups]);

  // Debug info for client ID
  const currentClientId = useMemo(() => {
    const currentMatterId = matterIds[activeTab];
    if (!currentMatterId || !matterGroups[currentMatterId]) return null;

    return matterGroups[currentMatterId].client;
  }, [matterIds, activeTab, matterGroups]);

  const handleSkipToNext = () => {
    if (activeTab < matterIds.length - 1) {
      setActiveTab(activeTab + 1);
    } else {
      navigate("/dashboard/bills");
      closeDialog();
    }
  };

  if (Object.keys(matterGroups).length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          No data available to generate bills
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {matterIds.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {matterIds.map((matterId, i) => (
                <Tab
                  key={matterId}
                  label={
                    matterGroups[matterId]?.matterName || `Matter ${matterId}`
                  }
                  id={`matter-tab-${i}`}
                />
              ))}
            </Tabs>
          </Box>
        )}

        <Box
          sx={{
            p: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {matterGroups[matterIds[activeTab]]?.allGenerated && (
            <Alert severity="info">
              All bills for matter "
              {matterGroups[matterIds[activeTab]]?.matterName}" have already
              been generated.
            </Alert>
          )}

          {matterGroups[matterIds[activeTab]]?.billGenerated &&
            !matterGroups[matterIds[activeTab]]?.allGenerated && (
              <Alert severity="warning">
                Some entries for matter "
                {matterGroups[matterIds[activeTab]]?.matterName}" have already
                been billed. Only non-billed entries will be included in this
                draft.
              </Alert>
            )}

          <Alert severity="info">
            Non-billable entries will appear in the draft only when "Show Entry
            on Bill" is enabled. Entries hidden here will also be hidden from
            the final bill preview.
          </Alert>

          {/* <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Client ID: {currentClientId || "N/A"}
          </Typography> */}

          <Controller
            name="issue_date"
            control={control}
            rules={{ required: "Issue Date is required" }}
            defaultValue={dayjs().format("YYYY-MM-DD")}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                defaultValue={dayjs()}
                value={field.value ? dayjs(field.value) : null}
                onChange={(newValue) => {
                  if (newValue && dayjs(newValue).isValid()) {
                    field.onChange(dayjs(newValue).format("YYYY-MM-DD"));
                  } else {
                    field.onChange(null);
                  }
                }}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    label: (
                      <span>
                        Issue Date<span style={{ color: "red" }}>*</span>
                      </span>
                    ),
                    size: "small",
                    variant: "outlined",
                    fullWidth: true,
                    error: !!errors?.issue_date,
                    helperText: !!errors?.issue_date?.message,
                  },
                }}
                format="MM/DD/YYYY"
              />
            )}
          />

          <Controller
            name="due_date"
            control={control}
            rules={{ required: "Due Date is required" }}
            defaultValue={dayjs().format("YYYY-MM-DD")}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                defaultValue={dayjs()}
                value={field.value ? dayjs(field.value) : null}
                onChange={(newValue) => {
                  if (newValue && dayjs(newValue).isValid()) {
                    field.onChange(dayjs(newValue).format("YYYY-MM-DD"));
                  } else {
                    field.onChange(null);
                  }
                }}
                sx={{ width: "100%" }}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    label: (
                      <span>
                        Due Date<span style={{ color: "red" }}>*</span>
                      </span>
                    ),
                    size: "small",
                    variant: "outlined",
                    fullWidth: true,
                    error: !!errors?.due_date,
                    helperText: !!errors?.due_date?.message,
                  },
                }}
                format="MM/DD/YYYY"
              />
            )}
          />

          {matterIds.length > 1 && (
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Drafting Bill for Matter:{" "}
              {matterGroups[matterIds[activeTab]]?.matterName}
              (Bill {activeTab + 1} of {matterIds.length})
            </Typography>
          )}

          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Summary of the Bill
          </Typography>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead sx={{ whiteSpace: "nowrap" }}>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      sx={{ fontWeight: 550, fontSize: "0.8rem" }}
                    >
                      <Tooltip title={col.headerName} arrow>
                        <span>{col.headerName}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ whiteSpace: "nowrap" }}>
                {currentMatterData.length > 0 ? (
                  currentMatterData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: row.bill_genrated
                          ? "rgba(0, 0, 0, 0.04)"
                          : "inherit",
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{
                            fontSize: "0.7rem",
                            color: row.bill_genrated
                              ? "rgba(0, 0, 0, 0.6)"
                              : "inherit",
                          }}
                        >
                          <Tooltip title={row[col.field] ?? "-"} arrow>
                            <span>{row[col.field] ?? "-"}</span>
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No Data Available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Total Billable Amount: ${totalBillableAmount}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
          }}
        >
          <Button variant="outlined" size="small" onClick={() => closeDialog()}>
            Cancel
          </Button>

          {/* Skip button for matters with all generated bills */}
          {!currentMatterHasBillableEntries &&
            activeTab < matterIds.length - 1 && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleSkipToNext}
              >
                Skip to Next Matter
              </Button>
            )}

          {/* Regular submit button - disabled if all bills are generated */}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="small"
            disabled={!currentMatterHasBillableEntries}
          >
            {matterIds.length > 1 && activeTab < matterIds.length - 1
              ? `Draft Bill & Continue to Next Matter`
              : `Draft Bill`}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default BillGenerateForm;
